import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Copy, Home, Wrench, AlertTriangle, HelpCircle, Loader2 } from "lucide-react";
import { usePreDevis } from "@/hooks/usePreDevis";
import { SERVICE_LABEL } from "@/components/Reviews";

const euro = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;

const ModeButton = ({ active, onClick, icon: Icon, label, hint, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`flex-1 min-w-0 text-left p-3 rounded-xl border transition-colors ${active ? "border-cyan-400 bg-cyan-400/10 text-cyan-200" : "border-line text-slate-300 hover:border-slate-500"}`}>
    <span className="flex items-center gap-2 font-bold text-sm"><Icon size={15} />{label}</span>
    <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>
  </button>
);

const ModePicker = ({ pd }) => {
  const isDomicile = pd.mode === "domicile";
  return (
    <div className="mt-5">
      <p className="font-mono-ui text-[11px] uppercase text-slate-500 mb-2">Mode d'intervention</p>
      <div className="flex gap-3">
        <ModeButton active={!isDomicile} onClick={() => pd.setMode("sur_place")} icon={Wrench} label="Sur place" hint="Le client apporte à l'atelier" testid="pre-devis-mode-sur-place" />
        <ModeButton active={isDomicile} onClick={() => pd.setMode("domicile")} icon={Home} label="À domicile" hint="Pépé se déplace" testid="pre-devis-mode-domicile" />
      </div>
      {isDomicile && (
        <div className="mt-3">
          <label className="font-mono-ui text-[11px] uppercase text-slate-500">Distance aller depuis Rosny (km)</label>
          <input type="number" min="0" value={pd.distance} onChange={(e) => pd.setDistance(e.target.value)} placeholder="ex : 12"
            className="mt-1 w-full bg-ink border border-line rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:border-cyan-400 outline-none" data-testid="pre-devis-distance-input" />
        </div>
      )}
    </div>
  );
};

const BulletList = ({ icon: Icon, title, items, testid, render }) => items?.length > 0 && (
  <div>
    <h4 className="font-mono-ui text-[11px] uppercase text-slate-500 mb-1 flex items-center gap-1.5">{Icon && <Icon size={12} />}{title}</h4>
    <ul className="space-y-1" data-testid={testid}>
      {items.map((item, i) => <li key={`${testid}-${render(item).key}`} className={render(item).className}>{render(item).node}</li>)}
    </ul>
  </div>
);

const ChiffrageCard = ({ result }) => {
  const c = result.chiffrage;
  return (
    <div className="rounded-xl border border-line bg-panel p-4" data-testid="pre-devis-chiffrage">
      <h4 className="font-mono-ui text-[11px] uppercase text-slate-500 mb-3">Chiffrage estimé ({result.heures_estimees} h · {result.fourchette_heures})</h4>
      <dl className="space-y-1.5 text-sm">
        <div className="flex justify-between"><dt className="text-slate-400">Main-d'œuvre</dt><dd data-testid="pre-devis-cout-mo">{euro(c.cout_main_oeuvre)}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-400">Déplacement {c.mode === "domicile" ? `(${c.distance_km} km aller)` : "(sur place)"}</dt><dd data-testid="pre-devis-cout-deplacement">{euro(c.cout_deplacement)}</dd></div>
        {c.minimum_applique && <div className="flex justify-between text-slate-400"><dt>Minimum d'intervention appliqué</dt><dd>{euro(c.minimum_intervention)}</dd></div>}
        <div className="flex justify-between border-t border-line pt-2 mt-2 font-bold text-cyan-300"><dt>Total estimé (hors pièces)</dt><dd data-testid="pre-devis-total">{euro(c.total_estime)}</dd></div>
      </dl>
      <p className="text-[11px] text-slate-500 mt-2">Les pièces ne sont pas chiffrées : ajoute-les toi-même.</p>
    </div>
  );
};

const ResultBlock = ({ result }) => (
  <div className="mt-6 space-y-5" data-testid="pre-devis-result">
    <p className="text-[11px] uppercase tracking-wide text-amber-300 border border-amber-400/30 bg-amber-400/10 rounded-lg px-3 py-2" data-testid="pre-devis-disclaimer">
      Estimation indicative à confirmer — brouillon à relire et valider avant tout envoi.
    </p>
    {result.grille_incomplete && (
      <p className="text-[11px] text-orange-300 border border-orange-400/30 rounded-lg px-3 py-2" data-testid="pre-devis-grid-warning">
        Ta grille tarifaire est incomplète (onglet « Tarifs »). Le calcul reste partiel tant que les tarifs ne sont pas renseignés.
      </p>
    )}
    <div>
      <h4 className="font-mono-ui text-[11px] uppercase text-slate-500 mb-1">Diagnostic</h4>
      <p className="text-sm text-slate-200 whitespace-pre-wrap" data-testid="pre-devis-diagnostic">{result.diagnostic}</p>
    </div>
    <BulletList title="Pièces probables (à chiffrer)" items={result.pieces} testid="pre-devis-pieces"
      render={(p) => ({ key: `${p.label}-${p.remarque}`, className: "text-sm text-slate-300", node: <><span className="text-cyan-300">•</span> {p.label}{p.remarque ? <span className="text-slate-500"> — {p.remarque}</span> : null}</> })} />
    <BulletList icon={HelpCircle} title="Questions à poser au client" items={result.questions} testid="pre-devis-questions"
      render={(q) => ({ key: q, className: "text-sm text-amber-200", node: `— ${q}` })} />
    <BulletList icon={AlertTriangle} title="Points incertains" items={result.incertitudes} testid="pre-devis-incertitudes"
      render={(x) => ({ key: x, className: "text-sm text-slate-400", node: `— ${x}` })} />
    <ChiffrageCard result={result} />
    <div>
      <h4 className="font-mono-ui text-[11px] uppercase text-slate-500 mb-1">Brouillon de message client</h4>
      <p className="text-sm text-slate-200 whitespace-pre-wrap bg-ink/60 border border-line rounded-xl p-3" data-testid="pre-devis-brouillon">{result.brouillon_client}</p>
    </div>
  </div>
);

export const PreDevisModal = ({ request, onClose }) => {
  const pd = usePreDevis();
  return (
    <AnimatePresence>
      {request && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-ink/80 backdrop-blur-sm" onClick={onClose} data-testid="pre-devis-overlay">
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }} transition={{ duration: 0.3 }}
            className="glass border border-line rounded-2xl w-full max-w-lg p-6 sm:p-8 max-h-[90dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" data-testid="pre-devis-modal" data-lenis-prevent>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono-ui text-[11px] uppercase text-pink-300 flex items-center gap-1.5"><Sparkles size={12} />Assistant pré-devis</p>
                <h3 className="font-display uppercase text-2xl mt-2">{request.name} · {SERVICE_LABEL[request.service] || request.service}</h3>
              </div>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-white transition-colors" aria-label="Fermer" data-testid="pre-devis-close"><X size={22} /></button>
            </div>

            <p className="mt-3 text-sm text-slate-400 whitespace-pre-wrap bg-ink/50 border border-line rounded-xl p-3 max-h-32 overflow-y-auto">{request.message}</p>

            <ModePicker pd={pd} />

            <button type="button" onClick={() => pd.generate(request)} disabled={pd.loading} data-testid="pre-devis-generate-button"
              className="mt-5 w-full py-3.5 rounded-full bg-pink-500 text-white font-bold uppercase text-sm hover:scale-[1.02] transition-transform disabled:opacity-50 flex items-center justify-center gap-2" style={{ boxShadow: "0 0 26px rgba(236,72,153,0.4)" }}>
              {pd.loading ? <><Loader2 size={15} className="animate-spin" />Pépé réfléchit…</> : <><Sparkles size={15} />{pd.result ? "Régénérer" : "Préparer le pré-devis"}</>}
            </button>

            {pd.result && <ResultBlock result={pd.result} />}

            {pd.result && (
              <button type="button" onClick={pd.copyDraft} data-testid="pre-devis-copy-button"
                className="mt-5 w-full py-3 rounded-full border border-line text-sm hover:text-cyan-300 transition-colors flex items-center justify-center gap-2">
                <Copy size={14} />Copier le brouillon client
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
